import React from 'react';
import { Github, Linkedin, Twitter, Mail, Heart } from 'lucide-react';
import { portfolioData } from '../mock';
import { portfolioDataId } from '../data/portfolioDataId';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { language, t } = useLanguage();
  const pd = language === 'id' ? portfolioDataId : portfolioData;
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Github,   url: portfolioData.social.github,   label: 'GitHub'   },
    { icon: Linkedin, url: portfolioData.social.linkedin, label: 'LinkedIn' },
    { icon: Twitter,  url: portfolioData.social.twitter,  label: 'Twitter'  },
    { icon: Mail,     url: `mailto:${portfolioData.email}`, label: 'Email'  },
  ];

  const navSections = [
    { key: 'home',      id: 'home'      },
    { key: 'about',     id: 'about'     },
    { key: 'projects',  id: 'projects'  },
    { key: 'services',  id: 'services'  },
    { key: 'skills',    id: 'skills'    },
    { key: 'contact',   id: 'contact'   },
  ];

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">

        {/* Top grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">

          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Arif Wahyudi" className="h-10 w-auto object-contain" />
              <div>
                <p className="font-bold text-gray-900 text-base leading-tight">Arif Wahyudi</p>
                <p className="text-xs font-semibold tracking-widest uppercase bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  IT Professional
                </p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">
              {pd.tagline}
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3 pt-1">
              {socialLinks.map(({ icon: Icon, url, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-emerald-600 hover:border-emerald-300 hover:shadow-sm transition-all duration-200"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gray-900 font-bold text-sm uppercase tracking-widest mb-4">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-2.5">
              {navSections.map(({ key, id }) => (
                <li key={id}>
                  <button
                    onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-gray-500 hover:text-emerald-600 transition-colors text-sm flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-emerald-400 transition-colors" />
                    {t.nav[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-gray-900 font-bold text-sm uppercase tracking-widest mb-4">
              {t.footer.getInTouch}
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${portfolioData.email}`}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-600 transition-colors"
                >
                  <Mail size={13} className="flex-shrink-0 text-emerald-400" />
                  {portfolioData.email}
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-500">
                <span className="text-emerald-400 text-xs">📞</span>
                {portfolioData.phone}
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-500">
                <span className="text-emerald-400 text-xs">📍</span>
                {portfolioData.location}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-400 text-xs">
            © {currentYear} Arif Wahyudi. {t.footer.madeWith}{' '}
            <Heart size={11} className="text-emerald-500 inline align-middle" />{' '}
            {t.footer.andCreativity}.
          </p>
          <p className="text-gray-300 text-xs">
            contact@ariftech.my.id
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
